import React, { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Save, FileDown, FileX, FileCheck, Printer, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import DashboardLayout from '../../components/DashboardLayout';
import api from '../../api';
import { formDef, formFields, isTherapy, THERAPY_KINDS, VISIT_ONLY } from '../../utils/clinicalForms';
import { TherapyGoalTracker } from '../../components/TherapyGoalTracker';
import { buildClinicalFormPdf } from '../../utils/clinicalFormPdf';
import { buildOrderPdf } from '../../utils/orderPdf';
// AI placeholder text such as "Not available in source document" is treated as empty
const PLACEHOLDER = /^\s*not (available|provided|specified|documented|stated|mentioned|found|recorded)\b/i;
const usDob = (v) => { const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v || '')); return m ? `${m[2]}/${m[3]}/${m[1]}` : String(v || ''); };

const inputCls = 'w-full border border-[#93a8e0] rounded px-2 py-1.5 text-sm bg-[#eef3fe] focus:bg-white focus:ring-2 focus:ring-[#1e40af] outline-none';

const Choice = ({ fd, value, onChange }) => {
  const on = Array.isArray(value) ? value : value ? [value] : [];
  const toggle = (o) => onChange(fd.multi ? (on.includes(o) ? on.filter(x => x !== o) : [...on, o]) : (on.includes(o) ? '' : o));
  return (
    <div className="flex flex-wrap gap-1.5">
      {fd.opts.map(o => (
        <button type="button" key={o} onClick={() => toggle(o)} data-testid={`cf-${fd.k}-${o.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
          className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${on.includes(o) ? 'bg-[#1e40af] text-white border-[#1e40af]' : 'bg-white text-gray-700 border-gray-300 hover:border-[#1e40af]'}`}>{o}</button>
      ))}
    </div>
  );
};

const FieldInput = ({ fd, value, onChange }) => {
  if (fd.opts) return <Choice fd={fd} value={value} onChange={onChange} />;
  if (fd.area) return <textarea rows={3} className={inputCls} value={value || ''} onChange={e => onChange(e.target.value)} data-testid={`cf-${fd.k}`} />;
  return <input type={fd.date ? 'date' : 'text'} className={inputCls} value={value || ''} onChange={e => onChange(e.target.value)} data-testid={`cf-${fd.k}`} />;
};

const toIso = (v) => { const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(String(v || '')); return m ? `${m[3]}-${m[1]}-${m[2]}` : v; };

// Generic screen for agency clinical forms (WATFS, PT/OT/ST evaluations): pre-filled from AI / upload / saved draft
export default function ClinicalFormPage() {
  const { formType } = useParams();
  const [params, setParams] = useSearchParams();
  const patientId = params.get('patientId');
  const kind = isTherapy(formType) && THERAPY_KINDS[params.get('kind')] ? params.get('kind') : 'initial';
  const storeAs = `${formType}${THERAPY_KINDS[kind].suffix}`;
  const def = formDef(formType, kind);
  const [patient, setPatient] = useState(null);
  const [v, setV] = useState({});
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(0);
  const aiRef = useRef(undefined);

  useEffect(() => {
    if (!def) return;
    window.scrollTo(0, 0);
    setV({});
    if (aiRef.current?.type !== formType) {  // read once per form: StrictMode runs this effect twice
      aiRef.current = { type: formType, data: JSON.parse(sessionStorage.getItem(`clinicalForm:${formType}`) || 'null') };
      sessionStorage.removeItem(`clinicalForm:${formType}`);
    }
    (async () => {
      const keys = new Set(formFields(formType, kind).map(f => f.k));
      const dates = new Set(formFields(formType, kind).filter(f => f.date).map(f => f.k));
      const get = (t) => api.get(`/admission-forms/${patientId}/${t}`).then(r => r.data?.form?.data || null).catch(() => null);
      const pick = (o) => Object.fromEntries(Object.entries(o || {}).filter(([k, x]) => keys.has(k) && x !== '' && x != null && !(Array.isArray(x) && !x.length) && !PLACEHOLDER.test(String(x))).map(([k, x]) => [k, dates.has(k) ? toIso(x) : x]));
      let p = null;
      let saved = {};
      if (patientId) {
        p = await api.get(`/patients/${patientId}`).then(r => r.data.patient || r.data).catch(() => null);
        saved = (await get(storeAs)) || {};
        if (kind !== 'initial' && !Object.keys(saved).length) saved = await startFromLast(get);
      }
      setPatient(p);
      const allergies = (p?.allergies || []).join?.(', ') || '';
      setV({ ...pick({ allergies, contraindications: allergies, primaryDiagnosis: p?.primaryDiagnosis, socDate: toIso(p?.startOfCareDate || '') }), ...(kind === 'initial' ? pick(aiRef.current.data) : {}), ...pick(saved) });
    })();
  }, [formType, patientId, kind]); // eslint-disable-line react-hooks/exhaustive-deps

  // New re-evaluation / discharge: copy the last evaluation (not its visit details) and the goal tracker statuses
  async function startFromLast(get) {
    const last = (kind === 'discharge' && await get(`${formType}_reeval`)) || (await get(formType)) || {};
    const base = Object.fromEntries(Object.entries(last).filter(([k]) => !VISIT_ONLY.includes(k)));
    const goals = await api.get(`/therapy-goals/${patientId}`).then(r => r.data.goals || []).catch(() => []);
    const lines = goals.filter(g => `${g.discipline}_evaluation` === formType)
      .map(g => `${g.term}: ${g.text} - ${g.latest ? `${g.latest.status} (${g.latest.visitDate})${g.latest.note ? `: ${g.latest.note}` : ''}` : 'not yet recorded'}`);
    return { ...base, evaluationType: THERAPY_KINDS[kind].label, goalStatus: lines.join('\n') };
  }

  if (!def) return <DashboardLayout><p className="p-6">Unknown form.</p></DashboardLayout>;
  const set = (k) => (x) => setV(prev => ({ ...prev, [k]: x }));
  const save = async () => {
    if (!patientId) { toast.error('Open this form from a patient chart to save it'); return; }
    setSaving(true);
    try { await api.post('/admission-forms/save', { ...v, patientId, formType: storeAs }); toast.success(`${def.short} saved`); setSavedAt(Date.now()); }
    catch { toast.error('Could not save'); } finally { setSaving(false); }
  };
  const pdf = (mode) => {
    const blank = mode === 'blank';
    const p = blank ? {} : patient || {};
    // wound care order prints like every other order sheet
    const doc = formType === 'wound_care_order'
      ? buildOrderPdf('Wound Care Order', blank ? {} : v, { patientName: [p.firstName, p.lastName].filter(Boolean).join(' '), dateOfBirth: p.dateOfBirth, mrn: p.medicalRecordNumber,
        primaryDiagnosis: v.primaryDiagnosis || p.primaryDiagnosis, startOfCareDate: p.startOfCareDate || p.admissionDate, primaryPhysician: p.primaryPhysician, primaryPhysicianPhone: p.primaryPhysicianPhone }, 'wound')
      : buildClinicalFormPdf(formType, blank ? {} : v, p, kind);
    doc.fillMode = mode;
    doc.save(`${def.short.replace(/\s+/g, '_')}${blank ? '_blank' : `_${patient?.lastName || 'patient'}`}${mode === 'flat' ? '_filled' : ''}.pdf`);
  };
  const btn = 'flex items-center gap-2 px-4 py-2 rounded text-sm font-medium disabled:opacity-50';

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto p-4 space-y-4" data-testid={`clinical-form-${formType}`}>
        <div className="bg-[#1e40af] text-white rounded-lg p-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-lg font-bold">{def.title}</h1>
            <p className="text-xs text-white/80">{patient ? `${patient.firstName} ${patient.lastName} · DOB ${usDob(patient.dateOfBirth)} · MR# ${patient.medicalRecordNumber || ''}` : 'No patient selected'}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => pdf('blank')} className={`${btn} bg-white/10 hover:bg-white/20`} data-testid="cf-blank-pdf"><FileX className="w-4 h-4" /> Blank PDF</button>
            <button onClick={() => pdf('prefill')} className={`${btn} bg-white/10 hover:bg-white/20`} data-testid="cf-download-pdf"><FileDown className="w-4 h-4" /> Editable PDF</button>
            <button onClick={() => pdf('flat')} className={`${btn} bg-white/10 hover:bg-white/20`} data-testid="cf-filled-pdf"><FileCheck className="w-4 h-4" /> Filled PDF</button>
            <button onClick={() => pdf('print')} className={`${btn} bg-white/10 hover:bg-white/20`} data-testid="cf-print"><Printer className="w-4 h-4" /> Print</button>
            <button onClick={save} disabled={saving} className={`${btn} bg-white text-[#1e40af]`} data-testid="cf-save">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save</button>
          </div>
        </div>
        {isTherapy(formType) && (
          <div className="flex flex-wrap gap-2" data-testid="cf-kind-switch">
            {Object.entries(THERAPY_KINDS).map(([k, kd]) => (
              <button key={k} type="button" onClick={() => setParams(prev => { const n = new URLSearchParams(prev); if (k === 'initial') n.delete('kind'); else n.set('kind', k); return n; })}
                className={`px-4 py-1.5 rounded-full text-sm border transition-colors ${kind === k ? 'bg-[#1e40af] text-white border-[#1e40af]' : 'bg-white text-gray-700 border-gray-300 hover:border-[#1e40af]'}`}
                data-testid={`cf-kind-${k}`}>{kd.label}</button>
            ))}
          </div>
        )}
        {def.sections.map(sec => (
          <section key={sec.title} className="bg-white rounded-lg border border-[#c7d4f5] overflow-hidden">
            <h2 className="bg-[#1e40af] text-white text-sm font-semibold px-3 py-1.5">{sec.title}</h2>
            <div className="p-3 grid grid-cols-1 md:grid-cols-3 gap-3">
              {sec.fields.map(fd => (
                <div key={fd.k} className={`block text-xs text-gray-700 ${fd.opts || fd.area ? 'md:col-span-3' : ''}`}>
                  <span className="font-medium">{fd.l}</span>
                  <div className="mt-1"><FieldInput fd={fd} value={v[fd.k]} onChange={set(fd.k)} /></div>
                </div>
              ))}
            </div>
          </section>
        ))}
        {isTherapy(formType) && <TherapyGoalTracker key={savedAt} patientId={patientId} discipline={formType.slice(0, 2)} />}
      </div>
    </DashboardLayout>
  );
}
